import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — renders an inline <svg> with arbitrary content
 * supplied as an HTML string. Useful for embedding brand icons or
 * decorative shapes that don't exist in Lucide.
 *
 * YAML usage:
 *   - type: RawSvg
 *     width: 16
 *     height: 16
 *     viewBox: "0 0 24 24"
 *     stroke: white            # optional, default "currentColor"
 *     fill: none               # optional, default "none"
 *     strokeWidth: 2           # optional
 *     ariaLabel: "Facebook"    # optional
 *     attributes:
 *       class: "shrink-0"
 *     inner: |
 *       <path d="M18 2h-3a5..." />
 */
export const RawSvg = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const width = resolve(props.width) ?? 16
  const height = resolve(props.height) ?? 16
  const viewBox = resolve(props.viewBox) ?? '0 0 24 24'
  const stroke = resolve(props.stroke) ?? 'currentColor'
  const fill = resolve(props.fill) ?? 'none'
  const strokeWidth = resolve(props.strokeWidth) ?? 2
  const ariaLabel = resolve(props.ariaLabel)
  const inner = resolve(props.inner) ?? ''
  const attributes = useEvaluatedAttributes(props.attributes ?? {})

  return (
    <ActionDependant {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox={viewBox}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label={ariaLabel}
        role={ariaLabel ? 'img' : undefined}
        {...attributes}
        dangerouslySetInnerHTML={{ __html: inner }}
      />
    </ActionDependant>
  )
}
